import React, { useEffect, useRef, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import {
    Box,
    Button,
    Card,
    CircularProgress,
    InputAdornment,
    TextField,
    ToggleButton,
    ToggleButtonGroup,
    Typography,
    useTheme,
} from '@mui/material'
import { Add, Search } from '@mui/icons-material'

import { FlexDiv } from '../../components'
import { ResponsivePageContainer } from '../../components/PageContainers'
import { TenantMessageCard } from '../../components/TenantMessageCard/TenantMessageCard'
import { resolveRoute, AppRoutes } from '../../helpers/Routes'
import { useTenantMessages, MessageFilters } from '../../hooks/useTenantMessages'
import { NewMessageDialog } from './NewMessageDialog'

type ReadFilter = 'all' | 'unread' | 'read'

export const MessagesInbox = () => {
    const theme = useTheme()
    const navigate = useNavigate()
    const [dialogOpen, setDialogOpen] = useState(false)
    const [searchText, setSearchText] = useState('')
    const [readFilter, setReadFilter] = useState<ReadFilter>('all')
    const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null)

    const { messages, isLoading, isLoadingMore, hasMore, getMessages, loadMore, createConversation } =
        useTenantMessages()

    const buildFilters = (
        search: string,
        read: ReadFilter,
    ): MessageFilters => {
        const filters: MessageFilters = {}
        if (search.trim()) {
            filters.search = search.trim()
        }
        if (read === 'read') {
            filters.is_read = true
        } else if (read === 'unread') {
            filters.is_read = false
        }
        return filters
    }

    useEffect(() => {
        getMessages(1, buildFilters(searchText, readFilter))
    }, [])

    const handleSearchChange = (value: string) => {
        setSearchText(value)
        if (debounceRef.current) clearTimeout(debounceRef.current)
        debounceRef.current = setTimeout(() => {
            getMessages(1, buildFilters(value, readFilter))
        }, 400)
    }

    const handleReadFilterChange = (
        _: React.MouseEvent<HTMLElement>,
        newValue: ReadFilter | null,
    ) => {
        const value = newValue ?? 'all'
        setReadFilter(value)
        getMessages(1, buildFilters(searchText, value))
    }

    const handleNewMessage = async (subject: string, body: string) => {
        const msg = await createConversation(subject, body)
        setDialogOpen(false)
        navigate(
            resolveRoute(AppRoutes.messageDetail, ':id', msg.id),
        )
    }

    return (
        <ResponsivePageContainer
            title="Messages"
            subtitle="Messages from your apartment community."
        >
            <Box
                sx={{
                    maxWidth: '700px',
                    width: '100%',
                    marginBottom: theme.spacing(2),
                    display: 'flex',
                    flexDirection: { xs: 'column', sm: 'row' },
                    alignItems: { xs: 'stretch', sm: 'center' },
                    gap: theme.spacing(1.5),
                }}
            >
                <Button
                    variant="contained"
                    startIcon={<Add />}
                    onClick={() => setDialogOpen(true)}
                    sx={{
                        backgroundColor: theme.palette.primary.main,
                        color: 'white',
                        textTransform: 'none',
                        fontWeight: 600,
                        borderRadius: 2,
                        flexShrink: 0,
                        '&:hover': {
                            backgroundColor: theme.palette.primary.dark,
                        },
                    }}
                >
                    New Message
                </Button>

                <TextField
                    size="small"
                    placeholder="Search messages..."
                    value={searchText}
                    onChange={(e) => handleSearchChange(e.target.value)}
                    InputProps={{
                        startAdornment: (
                            <InputAdornment position="start">
                                <Search
                                    sx={{
                                        color: 'rgba(255,255,255,0.4)',
                                        fontSize: 20,
                                    }}
                                />
                            </InputAdornment>
                        ),
                    }}
                    sx={{
                        flex: 1,
                        '& .MuiOutlinedInput-root': {
                            color: 'rgba(255,255,255,0.85)',
                            backgroundColor: 'rgba(255,255,255,0.04)',
                            borderRadius: 2,
                            '& fieldset': {
                                borderColor: 'rgba(255,255,255,0.15)',
                            },
                            '&:hover fieldset': {
                                borderColor: 'rgba(255,255,255,0.25)',
                            },
                            '&.Mui-focused fieldset': {
                                borderColor: theme.palette.primary.main,
                            },
                        },
                    }}
                />

                <ToggleButtonGroup
                    value={readFilter}
                    exclusive
                    onChange={handleReadFilterChange}
                    size="small"
                    sx={{ flexShrink: 0 }}
                >
                    {(['all', 'unread', 'read'] as ReadFilter[]).map(
                        (value) => (
                            <ToggleButton
                                key={value}
                                value={value}
                                sx={{
                                    textTransform: 'none',
                                    fontWeight: 600,
                                    fontSize: '0.8rem',
                                    color: 'rgba(255,255,255,0.6)',
                                    borderColor: 'rgba(255,255,255,0.15)',
                                    px: 1.5,
                                    '&.Mui-selected': {
                                        color: '#fff',
                                        backgroundColor:
                                            'rgba(70, 176, 131, 0.15)',
                                        borderColor:
                                            theme.palette.primary.main,
                                        '&:hover': {
                                            backgroundColor:
                                                'rgba(70, 176, 131, 0.25)',
                                        },
                                    },
                                    '&:hover': {
                                        backgroundColor:
                                            'rgba(255,255,255,0.05)',
                                    },
                                }}
                            >
                                {value.charAt(0).toUpperCase() +
                                    value.slice(1)}
                            </ToggleButton>
                        ),
                    )}
                </ToggleButtonGroup>
            </Box>

            <Box
                sx={{
                    display: 'flex',
                    justifyContent: 'flex-start',
                    width: '100%',
                    alignItems: 'flex-start',
                }}
            >
                {isLoading ? (
                    <FlexDiv
                        style={{
                            justifyContent: 'center',
                            padding: theme.spacing(4),
                            width: '100%',
                        }}
                    >
                        <CircularProgress
                            size={40}
                            style={{ color: theme.palette.primary.main }}
                        />
                    </FlexDiv>
                ) : messages.length === 0 ? (
                    <Card
                        sx={{
                            bgcolor: 'rgba(138, 142, 140, 0.16)',
                            borderRadius: 2,
                            boxShadow: 'none',
                            border: '1px solid rgba(255,255,255,0.1)',
                            p: 3,
                            textAlign: 'center',
                            width: '100%',
                            maxWidth: '700px',
                        }}
                    >
                        <Typography
                            variant="h6"
                            sx={{ color: 'rgba(255,255,255,0.8)', mb: 1 }}
                        >
                            {searchText.trim() || readFilter !== 'all'
                                ? 'No messages found'
                                : 'No messages yet'}
                        </Typography>
                        <Typography
                            variant="body1"
                            sx={{ color: 'rgba(255,255,255,0.5)' }}
                        >
                            {searchText.trim() || readFilter !== 'all'
                                ? 'Try adjusting your search or filters.'
                                : 'You have no messages from your apartment.'}
                        </Typography>
                    </Card>
                ) : (
                    <Box sx={{ maxWidth: '700px', width: '100%' }}>
                        {messages.map((msg) => (
                            <TenantMessageCard
                                key={msg.id}
                                message={msg}
                                onClick={() =>
                                    navigate(
                                        resolveRoute(
                                            AppRoutes.messageDetail,
                                            ':id',
                                            msg.id,
                                        ),
                                    )
                                }
                            />
                        ))}
                        {hasMore && (
                            <FlexDiv
                                style={{
                                    justifyContent: 'center',
                                    padding: theme.spacing(2),
                                }}
                            >
                                <Button
                                    variant="outlined"
                                    onClick={loadMore}
                                    disabled={isLoadingMore}
                                    sx={{
                                        textTransform: 'none',
                                        fontWeight: 600,
                                        color: 'rgba(255,255,255,0.85)',
                                        borderColor:
                                            'rgba(255,255,255,0.2)',
                                        '&:hover': {
                                            borderColor:
                                                theme.palette.primary.main,
                                            backgroundColor:
                                                'rgba(255,255,255,0.05)',
                                        },
                                    }}
                                >
                                    {isLoadingMore
                                        ? 'Loading...'
                                        : 'Load More'}
                                </Button>
                            </FlexDiv>
                        )}
                    </Box>
                )}
            </Box>

            <NewMessageDialog
                open={dialogOpen}
                onClose={() => setDialogOpen(false)}
                onSend={handleNewMessage}
            />
        </ResponsivePageContainer>
    )
}
